// TASK-008: Project-level Tests view with checkbox selection + bulk PDF download
// with live progress dialog showing per-report status, ETA, and failure details.
// BUG-2026-0143 follow-up: the original hand-rolled MUI table was replaced with
// the shared EditableGrid (read-only) — house styling, column sorting, and the
// Excel-style per-column value filters, with selection scoped to visible rows.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, Divider, IconButton, LinearProgress, Snackbar,
  Stack, Typography,
} from '@mui/material';
import { CheckCircle, Close, ErrorOutline, HourglassTop, Print, TableChart } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import apiClient from '../../services/apiClient';
import {
  downloadBulkTestSheets, downloadSummaryTable,
  saveBlobToDisk, saveMergedReportsByType, isWorkOrderBatchReport,
} from '../../services/reportService';
import { listProjectTests, type ProjectTestRow } from '../../services/workOrderService';
import { EditableGrid } from '../../components/tables/EditableGrid';
import { projectTestsGridColumns } from './projectTestsColumns';

interface Props {
  projectKey: string;
  /** Human-readable ProjectId (URL segment) — names per-type report PDFs
   *  when a type-group spans more than one work order. */
  projectId?: string;
}

interface ReportJob {
  key: string;
  label: string;            // human-readable "WO-2026-002 / BH-101 / S-101-1"
  status: 'pending' | 'generating' | 'done' | 'failed';
  error?: string;
  blob?: Blob;
  fileName?: string;
  durationMs?: number;
}

export const TestsTabContent = ({ projectKey, projectId }: Props) => {
  const theme = useTheme();
  const [rows, setRows] = useState<ProjectTestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [printingSheets, setPrintingSheets] = useState(false);
  const [summaryRunning, setSummaryRunning] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' | 'warning' }>({
    open: false, msg: '', severity: 'success',
  });

  // ── Progress dialog state ──
  const [jobs, setJobs] = useState<ReportJob[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const cancelledRef = useRef(false);
  const startTimeRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listProjectTests(projectKey)
      .then((data) => { if (!cancelled) setRows(data); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectKey]);

  // Display-only derived fields so sorting and the column filters operate on
  // exactly what the user sees (blank stays blank → filterable as "(Blanks)").
  const gridRows = useMemo(
    () => rows.map((r) => ({
      ...r,
      SampleDisplay: r.SpecimenRef ? `${r.SampleId} / ${r.SpecimenRef}` : r.SampleId,
      TestDateDisplay: r.TestDate ? new Date(r.TestDate).toISOString().slice(0, 10) : '',
    })),
    [rows],
  );

  // ── Bulk print: server-rendered TEST SHEETS (BUG-2026-0161) — one merged
  // PDF of bench data-entry forms (saved values printed, blanks to write in),
  // opened in a new tab ready to print. Replaces the old in-browser
  // window.print() of the live Test Parameters view.
  const openBulkPrint = useCallback(async () => {
    const keys = rows
      .filter((r) => selected.has(r.WorkOrderDetailKey))
      .map((r) => r.WorkOrderDetailKey);
    if (keys.length === 0) return;
    setPrintingSheets(true);
    try {
      const { generated, skipped, combined } = await downloadBulkTestSheets(keys);
      // Batch-scoped sheets (Point Load) cover every selected specimen of a
      // work order in ONE sheet — explain the count instead of alarming.
      const combinedNote = combined && combined.count > 0
        ? ` (${combined.count} ${combined.testNames.join('/')} selection${combined.count !== 1 ? 's' : ''} share the batch sheet)`
        : '';
      if (skipped.length > 0) {
        const noSheet = skipped.filter((s) => s.reason.includes('no sheet'));
        const failed = skipped.length - noSheet.length;
        setSnack({
          open: true,
          severity: 'warning',
          msg: `${generated} sheet${generated !== 1 ? 's' : ''} generated${combinedNote} — skipped ${skipped.length}: `
            + `${noSheet.length > 0 ? `${noSheet.length} without a sheet (${[...new Set(noSheet.map((s) => s.testName))].join(', ')})` : ''}`
            + `${noSheet.length > 0 && failed > 0 ? ', ' : ''}${failed > 0 ? `${failed} failed` : ''}`,
        });
      } else {
        setSnack({ open: true, severity: 'success', msg: `${generated} test sheet${generated !== 1 ? 's' : ''} ready to print${combinedNote}` });
      }
    } catch (err: any) {
      setSnack({ open: true, severity: 'error', msg: err?.message || 'Test sheet generation failed' });
    } finally {
      setPrintingSheets(false);
    }
  }, [rows, selected]);

  // ── Summary Table Report: ONE consolidated Excel workbook for the whole
  // project — one sheet per test type (BTS / Direct Shear / TCS / UCS /
  // UCS with modulus), one row per tested sample, client-fill metadata
  // columns left blank. Covers ALL tests with data in scope (selection is
  // deliberately ignored — the deliverable is the project-wide table).
  const handleSummaryTable = useCallback(async () => {
    setSummaryRunning(true);
    try {
      const { skipped } = await downloadSummaryTable({ projectKey });
      setSnack({
        open: true,
        severity: skipped > 0 ? 'warning' : 'success',
        msg: skipped > 0
          ? `Summary table downloaded — ${skipped} test${skipped !== 1 ? 's' : ''} without data skipped`
          : 'Summary table downloaded',
      });
    } catch (err: any) {
      setSnack({ open: true, severity: 'error', msg: err?.message || 'Summary table generation failed' });
    } finally {
      setSummaryRunning(false);
    }
  }, [projectKey]);

  // ── Bulk generate with live progress ──
  const handleBulkPrint = useCallback(async () => {
    if (selected.size === 0) return;
    cancelledRef.current = false;
    startTimeRef.current = Date.now();

    // Build job queue from selected rows. BUG-2026-0198: batch-scoped report
    // types (Point Load) render the WHOLE work order from any of its WOD keys —
    // one report table already covers every specimen. Selecting all the PLT
    // rows must generate ONE report per work order, not one identical report
    // per selected row (which merged into a PDF full of duplicate tables).
    // Collapse those to the first row seen per work order, mirroring the
    // server-side bulk test-sheet dedup (reportController `workOrderBatch`).
    const selectedRows = rows.filter((r) => selected.has(r.WorkOrderDetailKey));
    const seenBatchWorkOrders = new Set<string>();
    let combinedCount = 0;
    const jobRows = selectedRows.filter((r) => {
      if (!isWorkOrderBatchReport(r.TestCode)) return true;
      const woKey = r.WorkOrderKey || r.WorkOrderDetailKey;
      if (seenBatchWorkOrders.has(woKey)) {
        combinedCount += 1;
        return false;
      }
      seenBatchWorkOrders.add(woKey);
      return true;
    });
    const initialJobs: ReportJob[] = jobRows.map((r) => ({
      key: r.WorkOrderDetailKey,
      label: `${r.WorkOrderId} / ${r.LocationId} / ${r.SampleId}${r.SpecimenRef ? ` (${r.SpecimenRef})` : ''}`,
      status: 'pending',
    }));
    setJobs(initialJobs);
    setDialogOpen(true);

    const completed: ReportJob[] = [...initialJobs];

    for (let i = 0; i < completed.length; i++) {
      if (cancelledRef.current) break;

      // Mark current as generating
      completed[i] = { ...completed[i], status: 'generating' };
      setJobs([...completed]);

      const t0 = Date.now();
      try {
        const resp = await apiClient.get(
          `/workorderdetails/${completed[i].key}/report`,
          { responseType: 'blob', timeout: 60000 },
        );
        const disposition = resp.headers['content-disposition'] || '';
        const match = disposition.match(/filename="(.+)"/);
        const fileName = match?.[1] || `report-${completed[i].key.slice(0, 8)}.pdf`;

        completed[i] = {
          ...completed[i],
          status: 'done',
          blob: resp.data as Blob,
          fileName,
          durationMs: Date.now() - t0,
        };
      } catch (err: any) {
        completed[i] = {
          ...completed[i],
          status: 'failed',
          error: err?.response?.data?.error || err?.message || 'Unknown error',
          durationMs: Date.now() - t0,
        };
      }
      setJobs([...completed]);
    }

    // ONE merged PDF **per test type**, reports in list order within each
    // (lab request: 3 test types selected → 3 PDF files). Each file is named
    // `<WO-or-project>_<TestType>_<date>_<time>.pdf`. A single selected
    // report keeps the server's own per-test filename.
    const successes = completed.filter((j) => j.status === 'done' && j.blob);
    // BUG-2026-0198: explain why N Point Load selections produced fewer reports
    // (one batch report already covers every specimen in a work order).
    const combinedNote = combinedCount > 0
      ? ` — ${combinedCount} Point Load selection${combinedCount !== 1 ? 's' : ''} share the work-order batch report`
      : '';
    if (successes.length > 0 && !cancelledRef.current) {
      try {
        if (successes.length === 1) {
          saveBlobToDisk(successes[0].blob!, successes[0].fileName!);
          setSnack({
            open: true,
            severity: 'success',
            msg: `Report downloaded${combinedNote}`,
          });
        } else {
          const rowByKey = new Map(selectedRows.map((r) => [r.WorkOrderDetailKey, r]));
          const files = await saveMergedReportsByType(
            successes.map((j) => ({
              blob: j.blob!,
              testName: rowByKey.get(j.key)?.TestName ?? 'Report',
              workOrderId: rowByKey.get(j.key)?.WorkOrderId,
            })),
            projectId || 'Tests',
          );
          setSnack({
            open: true,
            severity: 'success',
            msg: `${files} PDF file${files !== 1 ? 's' : ''} downloaded (${successes.length} report${successes.length !== 1 ? 's' : ''}, one file per test type)${combinedNote}`,
          });
        }
      } catch (err: any) {
        setSnack({ open: true, severity: 'error', msg: err?.message || 'Failed to merge report PDFs' });
      }
    }
  }, [rows, selected, projectId]);

  // ── Derived progress stats ──
  const doneCount = jobs.filter((j) => j.status === 'done').length;
  const failCount = jobs.filter((j) => j.status === 'failed').length;
  const finishedCount = doneCount + failCount;
  const totalCount = jobs.length;
  const pct = totalCount > 0 ? Math.round((finishedCount / totalCount) * 100) : 0;
  const isRunning = dialogOpen && finishedCount < totalCount && !cancelledRef.current;

  // ETA calculation
  const elapsed = dialogOpen ? (Date.now() - startTimeRef.current) / 1000 : 0;
  const avgPerReport = finishedCount > 0 ? elapsed / finishedCount : 0;
  const remaining = totalCount - finishedCount;
  const etaSec = finishedCount > 0 ? Math.round(avgPerReport * remaining) : 0;
  const etaStr = etaSec > 60 ? `~${Math.ceil(etaSec / 60)}m` : `~${etaSec}s`;

  const currentJob = jobs.find((j) => j.status === 'generating');

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  if (rows.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">No tests in this project yet.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {rows.length} test{rows.length !== 1 ? 's' : ''} • {selected.size} selected
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={summaryRunning
              ? <CircularProgress size={14} thickness={5} />
              : <TableChart sx={{ fontSize: 16 }} />}
            disabled={summaryRunning || isRunning}
            onClick={handleSummaryTable}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {summaryRunning ? 'Building Summary…' : 'Summary Table Report'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={printingSheets
              ? <CircularProgress size={14} thickness={5} />
              : <Print sx={{ fontSize: 16 }} />}
            disabled={selected.size === 0 || isRunning || printingSheets}
            onClick={openBulkPrint}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {printingSheets ? 'Preparing Sheets…' : `Print Test Sheets (${selected.size})`}
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={selected.size === 0 || isRunning}
            onClick={handleBulkPrint}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Generate Reports ({selected.size})
          </Button>
        </Stack>
      </Box>

      <EditableGrid
        columns={projectTestsGridColumns}
        data={gridRows as unknown as Record<string, unknown>[]}
        onRowChange={() => {}}
        onRowSave={() => {}}
        onSelectionChange={(keys) => setSelected(new Set(keys))}
        keyField="WorkOrderDetailKey"
        allowAdd={false}
        onAddRow={() => {}}
        readOnly
        enableColumnFilters
      />

      {/* ── Progress Dialog ── */}
      <Dialog open={dialogOpen} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
        onClose={() => { if (!isRunning) setDialogOpen(false); }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0.5 }}>
          <Typography variant="h6" fontWeight={700}>
            {isRunning ? 'Generating Reports…' : cancelledRef.current ? 'Cancelled' : failCount > 0 ? 'Completed with errors' : 'All reports generated'}
          </Typography>
          <IconButton size="small" onClick={() => {
            if (isRunning) cancelledRef.current = true;
            else setDialogOpen(false);
          }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {/* Summary bar */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {finishedCount} / {totalCount} reports
                {failCount > 0 && <Typography component="span" sx={{ color: 'error.main', ml: 1, fontSize: 'inherit' }}>({failCount} failed)</Typography>}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isRunning ? `${pct}% • ETA ${etaStr}` : `${pct}% • done`}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 8, borderRadius: 4,
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                '& .MuiLinearProgress-bar': { borderRadius: 4 },
              }}
            />
          </Box>

          {/* Current activity */}
          {currentJob && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, mb: 1.5,
              bgcolor: alpha(theme.palette.info.main, 0.06), borderRadius: 1,
            }}>
              <CircularProgress size={16} thickness={5} />
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                <b>Now:</b> {currentJob.label}
              </Typography>
            </Box>
          )}

          {/* Job list */}
          <Stack spacing={0.5} sx={{ maxHeight: 300, overflow: 'auto' }}>
            {jobs.map((j, i) => (
              <Box key={j.key} sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5,
                borderRadius: 0.5,
                bgcolor: j.status === 'failed' ? alpha(theme.palette.error.main, 0.06)
                  : j.status === 'done' ? alpha(theme.palette.success.main, 0.04)
                  : 'transparent',
              }}>
                {/* Icon */}
                {j.status === 'done' && <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />}
                {j.status === 'failed' && <ErrorOutline sx={{ fontSize: 16, color: 'error.main' }} />}
                {j.status === 'generating' && <CircularProgress size={14} thickness={5} />}
                {j.status === 'pending' && <HourglassTop sx={{ fontSize: 16, color: 'text.disabled' }} />}

                {/* Label */}
                <Typography variant="body2" sx={{
                  flex: 1, fontSize: '0.78rem',
                  color: j.status === 'pending' ? 'text.disabled' : 'text.primary',
                }}>
                  {i + 1}. {j.label}
                </Typography>

                {/* Duration / error */}
                {j.status === 'done' && j.durationMs != null && (
                  <Typography variant="caption" color="text.secondary">{(j.durationMs / 1000).toFixed(1)}s</Typography>
                )}
                {j.status === 'failed' && (
                  <Typography variant="caption" color="error.main" sx={{ maxWidth: 180, textAlign: 'right' }}>
                    {j.error}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>

          {/* Bottom actions */}
          {!isRunning && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
              {failCount > 0 && doneCount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 'auto' }}>
                  {doneCount} successful report{doneCount !== 1 ? 's' : ''} downloaded, one merged PDF per test type. Failed reports were skipped.
                </Typography>
              )}
              <Button size="small" onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
                Close
              </Button>
            </Box>
          )}

          {isRunning && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button size="small" color="error" onClick={() => { cancelledRef.current = true; }}
                sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};
